/**
 * Merge a live game snapshot into roundsData / games arrays by gameId.
 */

/**
 * @param {Record<string, unknown>} target
 * @param {Record<string, unknown>} snapshot
 */
const PERIOD_START_KEYS = new Set(['firstHalfStartedAt', 'secondHalfStartedAt']);

export function mergeLiveGameFields(target, snapshot) {
  if (!target || !snapshot) return target;
  const next = { ...target };
  const keys = [
    'status',
    'homeScore',
    'awayScore',
    'livePhase',
    'liveMinute',
    'liveExtraMinute',
    'firstHalfStartedAt',
    'secondHalfStartedAt',
    'terminalAt',
    'redCardsHome',
    'redCardsAway',
    'penaltyGoalsHome',
    'penaltyGoalsAway',
    'missedPenaltiesHome',
    'missedPenaltiesAway',
    'matchEvents'
  ];
  for (const k of keys) {
    if (snapshot[k] === undefined) continue;
    // Never wipe period-start anchors with null — FE clock depends on them.
    if (PERIOD_START_KEYS.has(k) && snapshot[k] == null && next[k] != null) continue;
    next[k] = snapshot[k];
  }
  // GAME_ENDED without terminalAt: stamp now so the 5-min listen window still applies.
  if (
    String(next.status || '').toLowerCase() === 'finished' &&
    !Number.isFinite(Number(next.terminalAt))
  ) {
    next.terminalAt = Date.now();
  }
  return next;
}

/**
 * @param {Record<string, unknown> | null | undefined} roundsData
 * @param {string} gameId
 * @param {Record<string, unknown>} snapshot
 */
export function mergeLiveGameIntoRoundsData(roundsData, gameId, snapshot) {
  if (!roundsData || !gameId || !snapshot) return roundsData;
  const id = String(gameId);
  let changed = false;
  const next = { ...roundsData };

  for (const key of ['previousRound', 'currentRound', 'nextRound', 'nextNextRound']) {
    const round = roundsData[key];
    if (!round?.games || !Array.isArray(round.games)) continue;
    const idx = round.games.findIndex((g) => String(g.gameId) === id);
    if (idx < 0) continue;
    const games = round.games.slice();
    games[idx] = mergeLiveGameFields(games[idx], snapshot);
    next[key] = { ...round, games };
    changed = true;
  }

  return changed ? next : roundsData;
}

/**
 * @param {Array<Record<string, unknown>>} games
 * @param {string} gameId
 * @param {Record<string, unknown>} snapshot
 */
export function mergeLiveGameIntoGamesList(games, gameId, snapshot) {
  if (!Array.isArray(games) || !gameId || !snapshot) return games;
  const id = String(gameId);
  const idx = games.findIndex((g) => String(g.gameId) === id);
  if (idx < 0) return games;
  const next = games.slice();
  next[idx] = mergeLiveGameFields(next[idx], snapshot);
  return next;
}

/**
 * Collect games from betting roundsData for listen checks.
 * @param {Record<string, unknown> | null | undefined} roundsData
 */
export function collectGamesFromRoundsData(roundsData) {
  if (!roundsData) return [];
  const out = [];
  for (const key of ['currentRound', 'nextRound', 'previousRound']) {
    const games = roundsData[key]?.games;
    if (Array.isArray(games)) out.push(...games);
  }
  return out;
}

/**
 * Live/status fields only — strips pick payload so merges cannot clobber drafts.
 * @param {Record<string, unknown>} game
 * @returns {Record<string, unknown>}
 */
export function liveSnapshotFromGame(game) {
  if (!game || typeof game !== 'object') return {};
  const snap = { ...game };
  delete snap.bets;
  delete snap.predictedHomeScore;
  delete snap.predictedAwayScore;
  delete snap.augment;
  delete snap.clashAugments;
  return snap;
}

/**
 * Patch live game fields from a server rounds payload into local roundsData (keeps bets).
 * @param {Record<string, unknown> | null | undefined} localRounds
 * @param {Record<string, unknown> | null | undefined} serverRounds
 */
export function mergeServerLiveFieldsIntoRoundsData(localRounds, serverRounds) {
  if (!localRounds || !serverRounds) return localRounds;
  let next = localRounds;
  for (const game of collectGamesFromRoundsData(serverRounds)) {
    if (!game?.gameId) continue;
    next = mergeLiveGameIntoRoundsData(next, game.gameId, liveSnapshotFromGame(game));
  }
  return next;
}
